import React, { useState } from "react"
import { connect } from 'react-redux';
// import {
//   Link
// } from "react-router-dom";
import ReactTooltip from 'react-tooltip';
import copy from 'copy-to-clipboard';
import { CSVLink } from "react-csv";
import { 
  PieChart, Pie, BarChart, Bar, Cell, XAxis,
  YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Brush, Sector
} from 'recharts';

import { timeStampToEasyTime, timeStampToFullTime } from '../../utils/time';
import { styleKeyword, findKeywordsAndTruncate, capitalizeFirst, cleanHtml } from '../../utils/text';
import { toHistogram } from '../../utils/numbers';

import SubmissionTypeIcon from "../reddit/submissiontypeicon";
import AnalysisSentimentShareModal from "../language/analysisSentimentShare";
// import AnalysisQuestionAnswerResultsShareModal from "../language/questionAnswerResultsShare";

// analysis results for a sentiment report
const AnalysisSentimentsResults = ({
  analysis,
  reset,
  currentUser,
}) => {
  const analysisResults = analysis.results;
  const analysisContext = analysis.results_context || []; // right now just posts, eventually maybe comments
  const analysisSummary = analysis.results.all?.sentiments?.summary;
  const focusKeyword = analysis.params_analysis.sentiment_focus_keyword;
  const [shareOpen, setShareOpen] = useState(false);
  const [sharedReportHash, setSharedReportHash] = useState(analysis.shared_report_hash); // set to local state in case they just shared
  const [prosConsFilter, setProsConsFilter] = useState([]); // list of strings

  // results to show are ones we have sentiment metrics for, and are in current results
  const resultIds = analysisContext.map(c => c.id);
  const sentimentsToInclude = Object.keys(analysisResults)
    .filter(k => resultIds.includes(k))
    .map(k => Object.assign(analysisResults[k], {id: k}));
  const resultsToInclude = analysisContext.filter(r => analysisResults[r.id]);

  // calculate aggregate sentiment metrics
  var aggregateSentiment = sentimentsToInclude.reduce((acc, curr) => {
    acc.sentiment_score += ['good', 'bad'].includes(curr.sentiments.sentiment) ? curr.sentiments.sentiment_score : 0;
    acc.positive += curr.sentiments.sentiment === 'good' ? 1 : 0;
    acc.negative += curr.sentiments.sentiment === 'bad' ? 1 : 0;
    acc.neutral += curr.sentiments.sentiment === 'neutral' ? 1 : 0;
    return acc;
  }, {sentiment_score: 0, positive: 0, negative: 0, neutral: 0});
  
  // only include scores if they are not neutral
  if (sentimentsToInclude.filter(s => ['good', 'bad'].includes(s.sentiments.sentiment)).length > 0) {
    aggregateSentiment.overall_rating = parseInt(aggregateSentiment.positive / (aggregateSentiment.positive + aggregateSentiment.negative) * 100);
  } else {
    aggregateSentiment.overall_rating = 50;
  }

  // calculate the timeline of sentiments by category
  var timestamps = [];
  resultsToInclude.forEach(r => {
    const name = r.timestamp_utc;
    const index = timestamps.map(r => r.name).indexOf(name);
    index === -1 ? timestamps.push({'name': name, 'count': 1}) : timestamps[index]['count'] += 1
  });
  timestamps = timestamps.sort((a,b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)); 

  // turn timestamps into a histogram based on buckets
  var timestampToChart = []
  if (timestamps.length){
    const TIME_BUCKETS = timestamps.length >= 50 ? 50 : 20;
    const valuesOnly = timestamps.map(t => t.name)
    const maxTimestamp = valuesOnly[timestamps.length - 1] + 1;
    const minTimestamp = valuesOnly[0];
    const interval = parseInt((maxTimestamp - minTimestamp) / TIME_BUCKETS);
    const timestampHistogram = toHistogram(valuesOnly, TIME_BUCKETS, interval);
    timestampToChart = timestampHistogram.map((c) => {
      const bucketResults = analysisContext.filter(r => r.timestamp_utc >= c.min && r.timestamp_utc < c.max);
      return {
        'label': `${timeStampToEasyTime(c.min)} - ${c.isLast ? `now` : timeStampToEasyTime(c.max)}`,
        'min': c.min,
        'max': c.max,
        'positive': bucketResults.filter(r => analysis.results[r.id].sentiments.sentiment === 'good').length,
        'negative': bucketResults.filter(r => analysis.results[r.id].sentiments.sentiment === 'bad').length,
        'neutral': bucketResults.filter(r => analysis.results[r.id].sentiments.sentiment === 'neutral').length,
        'positiveBlurbs': bucketResults.filter(r => analysis.results[r.id].sentiments.sentiment === 'good').map(r => ({'id': r.id, 'highlight': analysis.results[r.id].sentiments.highlight})),
        'negativeBlurbs': bucketResults.filter(r => analysis.results[r.id].sentiments.sentiment === 'bad').map(r => ({'id': r.id, 'highlight': analysis.results[r.id].sentiments.highlight})),
      }
    });
    timestampToChart = timestampToChart.map(c => {c.unfilteredCount = c.count - c.filteredCount; return c;})
  }

  // consolidate pros & cons
  let prosAndConsList = [];
  sentimentsToInclude.filter(s => ['good', 'bad'].includes(s.sentiments.sentiment)).forEach(s => {
    (s.sentiments.pros || []).forEach(p => {
      let index = prosAndConsList.findIndex(i => i.text === capitalizeFirst(p));
      if (index === -1){ 
        prosAndConsList.push({'text': capitalizeFirst(p), 'totalCount': 0, 'proCount': 0, 'conCount': 0, 'ids': []});
        index = prosAndConsList.length - 1;
      }
      prosAndConsList[index].proCount += 1;
      prosAndConsList[index].totalCount += 1;
      prosAndConsList[index].ids.push(s.id);
    });
    (s.sentiments.cons || []).forEach(c => {
      let index = prosAndConsList.findIndex(i => i.text === capitalizeFirst(c));
      if (index === -1){
        prosAndConsList.push({'text': capitalizeFirst(c), 'totalCount': 0, 'proCount': 0, 'conCount': 0, 'ids': []});
        index = prosAndConsList.length - 1;
      }
      prosAndConsList[index].conCount += 1;
      prosAndConsList[index].totalCount += 1;
      prosAndConsList[index].ids.push(s.id);
    });
  });
  const countProsAndConsToInclude = 12;
  prosAndConsList = prosAndConsList.sort((a,b) => b.totalCount - a.totalCount).slice(0, countProsAndConsToInclude);
  
  // get the highest & lowest sentiment posts and show them (filter optional)
  const countPostsToShow = 20;
  let postsToShow = sentimentsToInclude
    .filter(s => ['good', 'bad'].includes(s.sentiments.sentiment))
    .map(s => Object.assign(s, {
      'magnitude': Math.abs(s.sentiments.sentiment_score - 50),
      'post': resultsToInclude.find(r => r.id === s.id)
    }))
    .sort((a,b) => b.magnitude - a.magnitude)
  if (prosConsFilter.length > 0){
    postsToShow = postsToShow.filter(p => {
      return [...p.sentiments.pros, ...p.sentiments.cons].some(t => prosConsFilter.includes(capitalizeFirst(t)));
    });
  }
  postsToShow = postsToShow.slice(0, countPostsToShow);

  return (
    <div id="sentiments-analysis">

      <div className="mt-4">
        <div className="p-6 bg-gray-800 rounded-lg text-white">

          <div className="flex border-b border-gray-600 pb-4 mb-4 w-full">
            <h2 className="text-xl font-medium flex items-center flex-wrap">
              <div className="text-gray-100 font-semibold">{focusKeyword} sentiment</div>

              <ReactTooltip id={`action-icons`} place="top" effect="solid" backgroundColor="white" textColor="black" />
              <div className="ml-4 flex items-center space-x-2 text-gray-400">
                {reset ? (
                  <div className="hover:text-gray-200 cursor-pointer"
                    onClick={reset}
                    data-for={`action-icons`} data-tip={"Reset"}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                    </svg>
                  </div>
                ) : ''}
              </div>
            </h2>
            <div className="ml-auto flex-shrink-0 flex space-x-4">
              <CSVLink
                data={sentimentsToInclude.filter(s => s.post).filter(s => s.sentiments.sentiment !== "neutral").reverse().map(s => Object.assign({}, {
                  'sentiment': s.sentiments.sentiment,
                  'sentiment_score': s.sentiments.sentiment_score,
                  'highlight': s.sentiments.highlight?.replace(',', ''),
                  'pros': (s.sentiments.pros || []).join(' && '),
                  'cons': (s.sentiments.cons || []).join(' && '),
                  'timestamp': new Date(s.post.timestamp_utc * 1000).toISOString(),
                  'subreddit': s.post.subreddit_name,
                  'score': s.post.score,
                  'num_comments': s.post.num_comments,
                  'title': s.post.title?.replace(',', ''),
                  'link': `https://www.reddit.com${s.post.link}`,
                }))}
                filename={`gummysearch-sentiment-${focusKeyword}.csv`}
                className="ml-auto bg-gray-700 flex inline-block px-4 py-2 shadow-sm text-sm text-white font-medium rounded-md opacity-100 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
                target="_blank"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-1 mr-2 h-5 w-5 text-white">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                <span>Download</span>
              </CSVLink>

              <button type="button" id="share-results" className="bg-cyan-500 flex inline-block px-4 py-2 shadow-sm text-sm text-white font-medium rounded-md opacity-100 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
                onClick={() => setShareOpen(true)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="-ml-1 mr-2 h-5 w-5 text-white">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                </svg>
                <span>Share</span>
              </button>
            </div>
          </div>

          <div className="text-lg text-gray-200">
            {analysisSummary}
          </div>
        </div>
      </div>

      <div className="mt-4 flex space-x-4">
        <div className="h-28 flex-grow">
          <h2 className="text-md font-sm mb-0 mt-4 flex items-center">
            <span className="opacity-75">Timeline</span>
          </h2>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={timestampToChart}
            >
              <Tooltip content={({ active, payload, min, max }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  var minDate = timeStampToEasyTime(data.min || 0);
                  var maxDate = timeStampToEasyTime(data.max || 0);
                  if (minDate === maxDate){
                    minDate = timeStampToFullTime(data.min || 0);
                    maxDate = timeStampToFullTime(data.max || 0);
                  }

                  var countSummary = '';
                  if (data.positive > 0){countSummary += `${data.positive} positive`}
                  if (data.negative > 0){countSummary += `${countSummary ? ', ' : ''}${data.negative} negative`}
                  if (data.neutral > 0){countSummary += `${countSummary ? ', ' : ''}${data.neutral} neutral`}

                  return (
                    <div className="p-2 bg-gray-800 text-xs text-white rounded-md">
                      <div>{countSummary}</div>
                      <div>From {minDate} to {maxDate}</div>

                      {data.positiveBlurbs.length > 0 ? (
                        <div><br/>
                          <div className="font-semibold">Positive</div>
                          <ul>
                            {data.positiveBlurbs.map(b => <li key={b.id}>- {b.highlight}</li>)}
                          </ul>
                        </div>
                      ) : ''}
                      
                      {data.negativeBlurbs.length > 0 ? (
                        <div><br/>
                          <div className="font-semibold">Negative</div>
                          <ul>
                            {data.negativeBlurbs.map(b => <li key={b.id}>- {b.highlight}</li>)}
                          </ul>
                        </div>
                      ) : ''}
                    </div>
                  );
                }

                return null;
              }} cursor={{ stroke: "rgb(75 85 99)", strokeWidth: 1, fill: "rgb(31 41 55)" }}/>
              <Bar dataKey={"positive"} stackId="a" fill="#2ecc71"/>
              <Bar dataKey={"negative"} stackId="a" fill="#e74c3c"/>
              <Bar dataKey={"neutral"} stackId="a" fill="rgb(31 41 55)"/>
            </BarChart>
          </ResponsiveContainer>
          {timestampToChart.length ? (
            <div className="text-white flex items-center text-xs border-t border-gray-700">
              <div className="text-gray-500">{timeStampToEasyTime(timestampToChart[0].min)}</div>
              <div className="text-gray-500 ml-auto">
                {timeStampToEasyTime(timestampToChart[timestampToChart.length - 1].max)}
                {!timeStampToEasyTime(timestampToChart[timestampToChart.length - 1].max).includes('/') ? ' Today' : ''}
              </div>
            </div>
          ) : ''}
        </div>

        <div className="h-32 w-32 relative">
        <h2 className="text-md font-sm mb-0 mt-4 flex items-center">
            <span className="opacity-75">Overall</span>
          </h2>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={[
                  {'name': 'positive', 'percent': 100 * aggregateSentiment.positive / sentimentsToInclude.length, 'count': aggregateSentiment.positive},
                  {'name': 'negative', 'percent': 100 * aggregateSentiment.negative / sentimentsToInclude.length, 'count': aggregateSentiment.negative},
                  {'name': 'neutral', 'percent': 100 * aggregateSentiment.neutral / sentimentsToInclude.length, 'count': aggregateSentiment.neutral},
                ]}
                dataKey="percent" cx="50%" cy="50%"
                innerRadius={40} outerRadius={50}
                fill="rgb(31 41 55)" stroke="rgba(75, 85, 99, 0.25)"
                activeIndex={[0,1]} // 0,1 for positive & nagative, leave neutral out
                activeShape={(props) => {
                  const RADIAN = Math.PI / 180;
                  const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value, name } = props;
                  const sin = Math.sin(-RADIAN * midAngle);
                  const cos = Math.cos(-RADIAN * midAngle);
                  const mx = cx + (outerRadius + 30) * cos;
                  const my = cy + (outerRadius + 30) * sin;
                  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
                  const ey = my;

                  return (
                    <g>
                      <text x={cx} y={cy} dy={4} textAnchor="middle" fill={'#fff'}
                        textDecoration={'0'}
                        fontSize={'16'}
                      >
                        {aggregateSentiment.overall_rating} / 100
                      </text>
                      <Sector
                        cx={cx}
                        cy={cy}
                        innerRadius={innerRadius}
                        outerRadius={outerRadius}
                        startAngle={startAngle}
                        endAngle={endAngle}
                        fill={name === 'positive' ? '#2ecc71' : name === 'negative' ? '#e74c3c' : '#8e44ad'}
                      />
                      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
                    </g>
                  );
                }}
              />
              
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-10 pt-2">
        <h2 className="text-md font-sm">
          <span className="opacity-75">Pros & Cons</span>
        </h2>
        <div className="flex flex-wrap">
          {prosAndConsList.map(p => (
            <div key={p.text} className={`flex items-center mb-1 rounded-md px-2 py-1 mr-1 mb-1 cursor-pointer ${prosConsFilter.includes(p.text) ? 'bg-gray-600' : 'bg-gray-800'}`}
              // data-for={`pros-cons-detail`} data-tip={`${p.highlights.join(', ')}`}
              onClick={() => {
                if (prosConsFilter.includes(p.text)){
                  setProsConsFilter(prosConsFilter.filter(f => f !== p.text));
                } else {
                  setProsConsFilter([...prosConsFilter, p.text]);
                }
              }}
            >
               <div className="text-gray-200 mr-1">{p.text}</div>
              {p.proCount > 0 ? (
                <React.Fragment>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-green-500 ml-2 mr-1">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
                  </svg>
                  <div className="text-gray-400 text-sm font-semibold">{p.proCount}</div>
                </React.Fragment>
              ) : ''}
              
              {p.conCount > 0 ? (
                <React.Fragment>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-red-500  ml-2 mr-1">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.498 15.25H4.372c-1.026 0-1.945-.694-2.054-1.715a12.137 12.137 0 0 1-.068-1.285c0-2.848.992-5.464 2.649-7.521C5.287 4.247 5.886 4 6.504 4h4.016a4.5 4.5 0 0 1 1.423.23l3.114 1.04a4.5 4.5 0 0 0 1.423.23h1.294M7.498 15.25c.618 0 .991.724.725 1.282A7.471 7.471 0 0 0 7.5 19.75 2.25 2.25 0 0 0 9.75 22a.75.75 0 0 0 .75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 0 0 2.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384m-10.253 1.5H9.7m8.075-9.75c.01.05.027.1.05.148.593 1.2.925 2.55.925 3.977 0 1.487-.36 2.89-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398-.306.774-1.086 1.227-1.918 1.227h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 0 0 .303-.54" />
                  </svg>
                  <div className="text-gray-400 text-sm font-semibold">{p.conCount}</div>
                </React.Fragment>
              ) : ''}
            </div>
          ))}
        </div>
      </div>


      <div className="mt-8 mb-10">
        {/* <ReactTooltip id={`pros-cons-detail`} place="top" effect="solid" backgroundColor="white" textColor="black" /> */}
        <h2 className="text-md font-sm mb-0 mt-4 flex items-center">
          <span className="opacity-75">Posts <span className="text-gray-400 text-xs ml-1">{postsToShow.length} / {sentimentsToInclude.filter(s => ['good', 'bad'].includes(s.sentiments.sentiment)).length}</span></span>
        </h2>
        <div>
          {postsToShow.map(p => (
            <div key={p.id} className="mt-3 mb-1">
              
              <div className="text-gray-200 text-sm flex flex-wrap items-center space-x-2">

                {p.sentiments.sentiment === 'good' ? (
                  <React.Fragment>
                    {[...Array(p.magnitude >= 45 ? 3 : p.magnitude >= 30 ? 2 : 1).keys()].map(i => (
                      <svg key={`plus-${i}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-green-500">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
                      </svg>
                    ))}
                  </React.Fragment>
                ) : p.sentiments.sentiment === 'bad' ? (
                  <React.Fragment>
                    {[...Array(p.magnitude >= 45 ? 3 : p.magnitude >= 30 ? 2 : 1).keys()].map(i => (
                      <svg key={`minus-${i}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-red-500">
                         <path strokeLinecap="round" strokeLinejoin="round" d="M7.498 15.25H4.372c-1.026 0-1.945-.694-2.054-1.715a12.137 12.137 0 0 1-.068-1.285c0-2.848.992-5.464 2.649-7.521C5.287 4.247 5.886 4 6.504 4h4.016a4.5 4.5 0 0 1 1.423.23l3.114 1.04a4.5 4.5 0 0 0 1.423.23h1.294M7.498 15.25c.618 0 .991.724.725 1.282A7.471 7.471 0 0 0 7.5 19.75 2.25 2.25 0 0 0 9.75 22a.75.75 0 0 0 .75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 0 0 2.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384m-10.253 1.5H9.7m8.075-9.75c.01.05.027.1.05.148.593 1.2.925 2.55.925 3.977 0 1.487-.36 2.89-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398-.306.774-1.086 1.227-1.918 1.227h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 0 0 .303-.54" />
                      </svg>
                    ))}
                  </React.Fragment>
                ) : ''}
                <span className={`text-gray-400 text-sm mr-2 ${p.sentiments.sentiment === 'good' ? 'text-green-500' : p.sentiments.sentiment === 'bad' ? 'text-red-500' : 'text-gray-400'}`}>
                  {p.magnitude >= 30 ? 'Very ' : ''}
                  {p.sentiments.sentiment === 'good' ? 'Positive' : p.sentiments.sentiment === 'bad' ? 'Negative' : 'Neutral'}
                </span>

                
                <div className="flex items-center space-x-2 text-xs text-gray-400">
                  {p.sentiments.pros.map(p => (
                    <div className="flex items-center rounded-md py-0.5 px-2 bg-gray-800" key={p}>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4 mr-1">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
                      </svg>
                      <div>{capitalizeFirst(p)}</div>
                    </div>
                  ))}
                  {p.sentiments.cons.map(c => (
                    <div className="flex items-center rounded-md py-1 px-2 bg-gray-800" key={c}>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4 mr-1">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.498 15.25H4.372c-1.026 0-1.945-.694-2.054-1.715a12.137 12.137 0 0 1-.068-1.285c0-2.848.992-5.464 2.649-7.521C5.287 4.247 5.886 4 6.504 4h4.016a4.5 4.5 0 0 1 1.423.23l3.114 1.04a4.5 4.5 0 0 0 1.423.23h1.294M7.498 15.25c.618 0 .991.724.725 1.282A7.471 7.471 0 0 0 7.5 19.75 2.25 2.25 0 0 0 9.75 22a.75.75 0 0 0 .75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 0 0 2.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384m-10.253 1.5H9.7m8.075-9.75c.01.05.027.1.05.148.593 1.2.925 2.55.925 3.977 0 1.487-.36 2.89-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398-.306.774-1.086 1.227-1.918 1.227h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 0 0 .303-.54" />
                      </svg>
                      <div>{capitalizeFirst(c)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <a href={`${p.post.link.includes('https://') ? p.post.link : `https://reddit.com${p.post.link}`}`}
                rel="nofollow"
                className="text-sm text-gray-200 hover:underline singleLineClip containsKeywords my-1" target="_blank"
                  dangerouslySetInnerHTML={{__html: cleanHtml(styleKeyword(
                    findKeywordsAndTruncate(p.post.title, p.sentiments.highlight, 180),
                    p.sentiments.highlight,
                    'bg-transparent border-b border-dashed text-gray-200 border-cyan-700'))
                  }}
              />

              {/* <div className="text-blue-500 text-sm">"{p.sentiments.highlight}"</div> */}
              <div className="text-sm text-gray-400 containsKeywords doubleLineClip mb-1"
                dangerouslySetInnerHTML={{__html: cleanHtml(styleKeyword(
                  findKeywordsAndTruncate(p.post.body, p.sentiments.highlight, 300),
                  p.sentiments.highlight,
                  'bg-transparent border-b border-dashed text-gray-300 border-cyan-700'))
                }}
              />
              <div className="flex items-center flex-wrap justify-end w-full space-x-2">

                <a href={`${p.post.link.includes('https://') ? p.post.link : `https://reddit.com${p.post.link}`}`} target="_blank">
                  <SubmissionTypeIcon type={p.post.type} className="h-5 w-5 text-gray-500 hover:text-white"/>
                </a>
                <span className="hidden sm:block mx-2" aria-hidden="true">&middot;</span>
                <span className="text-sm text-gray-500 font-medium truncate">
                  {p.post.subreddit_name}
                </span>
                <span className="hidden sm:block mx-2" aria-hidden="true">&middot;</span>
                <span className="text-sm text-gray-500 font-medium truncate">{timeStampToEasyTime(p.post.timestamp_utc)}</span>
              </div>
            </div>
          ))}
          {postsToShow.length === 0 ? 'No relevant posts found' : ''}
        </div>
      </div>

      {shareOpen ? (
        <AnalysisSentimentShareModal
          analysis={analysis}
          sharedReportHash={sharedReportHash} setSharedReportHash={setSharedReportHash} // keep local state b/c no refetch after share
          closeModal={() => setShareOpen(false)}
        />
      ) : ''}
    </div>
  );
}

export default AnalysisSentimentsResults;
